import React, { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { fbTrack } from "@/lib/fb-pixel";

/**
 * Reusable email capture form for any lead magnet.
 * onSubmit(name, email, consent) => Promise (rejects if backend fails)
 */
export default function LeadCaptureForm({
  onSubmit,
  ctaLabel = "Get my report",
  helperText = "We'll never share your email. Unsubscribe anytime.",
  fbEvent = "Lead",
  fbEventParams = {},
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Please enter your name");
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return setError("Please enter a valid email");
    setSubmitting(true);
    try {
      await onSubmit(name.trim(), email.trim().toLowerCase(), consent);
      fbTrack(fbEvent, fbEventParams);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-medium mb-2">
          Your name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Margaret Davies"
          className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-[var(--ink)] focus:outline-none focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 transition"
          data-testid="lead-input-name"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-medium mb-2">
          Email
        </label>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.co.uk"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-black/10 bg-white text-[var(--ink)] focus:outline-none focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 transition"
            data-testid="lead-input-email"
          />
        </div>
      </div>
      <label className="flex items-start gap-3 text-xs text-[var(--ink-soft)] cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[var(--terracotta)]"
          data-testid="lead-input-consent"
        />
        <span>
          Yes — keep me posted with helpful NLV tips and news from Carley. You can unsubscribe any time.
        </span>
      </label>

      {error && (
        <div className="text-sm text-[var(--terracotta-deep)] bg-[var(--terracotta)]/8 border border-[var(--terracotta)]/20 rounded-lg px-4 py-3" data-testid="lead-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full pill inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="lead-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {submitting ? "Sending…" : ctaLabel}
      </button>

      <p className="text-[0.7rem] text-[var(--muted)] text-center">{helperText}</p>
    </form>
  );
}
